import { FormEvent, forwardRef, type ComponentProps } from 'react';
import { css, cx } from '@linaria/atomic';
import { SearchIconSmall } from '../../../../icon';
import { IconButton } from '../../../icon-button';
import { Input } from '../../atoms/input.component';
import type { BaseInputProps } from '../../atoms/types';
import { FieldLayout } from '../../layouts/field-layout.component';
import type { BasicFormFieldProps, ExtraFieldProps, FormFieldProps } from '../../layouts/types';

const componentFieldLayoutStyle1 = css`
  border-radius: 20px;
  padding-left: 32px;
`;

const componentSearchIconSmallStyle2 = css`
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 16px;
  transition: color 200ms ease-out, left 250ms ease-out, transform 250ms ease-out;
  transform: translate(0%, -50%);
`;

const componentSearchIconSmallStyle3 = css`
  left: 50%;
  transform: translate(-50%, -50%);
`;

const componentIconButtonStyle4 = css`
  padding: 4px;
  width: 24px;
  min-width: 24px;
  height: 24px;
`;

const componentSearchIconSmallStyle5 = css`
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 16px;
  transition: color 200ms ease-out, left 250ms ease-out, transform 250ms ease-out;
  transform: translate(0%, -50%);
`;

const componentSearchIconSmallStyle6 = css`
  left: 50%;
  transform: translate(-50%, -50%);
`;

type SearchFieldProps = Omit<BasicFormFieldProps<'text'>, 'label'> &
  Pick<ExtraFieldProps, 'containerCss' | 'onClear'> &
  Omit<BaseInputProps, 'type'> & {
    label?: string;
    variant?: 'regular' | 'round';
    clearable?: boolean;
    onSearchSubmit?: (event?: FormEvent<HTMLFormElement>) => void;
    searchButtonDisabled?: boolean;
    fieldComponentProps?: FormFieldProps<'text'>['fieldComponentProps'];
  };

const ConditionalFormWrapper = ({ children, includeForm, onSearchSubmit }) =>
  includeForm ? (
    <form
      role='search'
      onSubmit={(e) => {
        e.preventDefault();
        onSearchSubmit(e);
      }}
    >
      {children}
    </form>
  ) : (
    children
  );

// eslint-disable-next-line react/display-name
export const SearchField = forwardRef<HTMLDivElement, SearchFieldProps>(
  (
    {
      label,
      placeholder = 'Search',
      variant = 'regular',
      onSearchSubmit,
      clearable = true,
      searchButtonDisabled = false,
      ...rest
    },
    ref
  ) => {
    const isRound = variant === 'round';
    const isActive = rest.active || !!rest.value;

    return (
      <ConditionalFormWrapper includeForm={!!onSearchSubmit} onSearchSubmit={onSearchSubmit}>
        <FieldLayout
          {...(rest as ComponentProps<typeof FieldLayout>)}
          ref={ref}
          clearable={clearable}
          className={cx(rest.className, isRound && componentFieldLayoutStyle1)}
          aria-label={isRound || !label ? 'Search' : undefined}
          label={isRound ? undefined : label}
          placeholder={isRound ? undefined : placeholder}
          startAdornment={
            onSearchSubmit ? undefined : (
              <SearchIconSmall
                className={cx(
                  isRound && componentSearchIconSmallStyle2,
                  isRound && !isActive && componentSearchIconSmallStyle3
                )}
              />
            )
          }
          startAdornmentWidth={16}
          endAdornment={
            onSearchSubmit && (
              <IconButton
                label='Search'
                type='submit'
                disabled={searchButtonDisabled}
                className={componentIconButtonStyle4}
              >
                <SearchIconSmall
                  className={cx(
                    isRound && componentSearchIconSmallStyle5,
                    isRound && !isActive && componentSearchIconSmallStyle6
                  )}
                />
              </IconButton>
            )
          }
          onSearchSubmit={onSearchSubmit}
          //TODO: this might be able to be improved. But this type cast just makes sure the field prop is any of the correct field types
          field={Input as ComponentProps<typeof FieldLayout>['field']}
        />
      </ConditionalFormWrapper>
    );
  }
);
